import { RETURN_METHOD, ReturnMethodType } from '../constants/general';
import {
  IReturnEligibilityResponse,
  IReturnEligibilityV2Response,
  IReturnMethodsResponse,
  IReturnMethodsShipToAddress,
} from '../models/api';
import { ShipToAddress } from '../models/finalize-return';
import {
  ItemEligibilities,
  ItemToReturn,
  OrderDetailsLineItemSessionObj,
  ReturnFormItem,
} from '../models/returns';
import { getFeatureSwitchStatic } from './general';
import { isROSEnabled } from '../constants/fs-values';

//* Return Allowed comes from OMS. */
export type ReturnAllowedFields =
  | 'returnOnlineAllowed'
  | 'returnInStoreAllowed'
  | 'returnLORPAllowed'
  | 'returnDropoffAllowed';
export const ReturnAllowedMap = {
  [RETURN_METHOD.UPS_DROP_OFF]: 'returnDropoffAllowed' as ReturnAllowedFields,
  // TODO remove this mapping when SORIS is live
  [RETURN_METHOD.IN_STORE]: 'returnInStoreAllowed' as ReturnAllowedFields,
  [RETURN_METHOD.LORP]: 'returnLORPAllowed' as ReturnAllowedFields,
  [RETURN_METHOD.SORIS]: 'returnInStoreAllowed' as ReturnAllowedFields,
  [RETURN_METHOD.APO_FPO]: 'returnDropoffAllowed' as ReturnAllowedFields,
};

//* Return Eligibility comes from CheckEligibilityAPI. */
export type EligibilityItemFields =
  | 'returnEligible'
  | 'sorisReturnEligible'
  | 'lorpReturnEligible';
export const CheckEligibilityMap = {
  [RETURN_METHOD.UPS_DROP_OFF]: 'returnEligible' as EligibilityItemFields,
  [RETURN_METHOD.IN_STORE]: 'sorisReturnEligible' as EligibilityItemFields,
  [RETURN_METHOD.LORP]: 'lorpReturnEligible' as EligibilityItemFields,
  [RETURN_METHOD.SORIS]: 'sorisReturnEligible' as EligibilityItemFields,
  [RETURN_METHOD.APO_FPO]: 'returnEligible' as EligibilityItemFields,
};

/**
 * Checks if lineItems are eligible for returns based on OMS eligibility and CheckEligibilityAPI response.
 * @param item Line item from order details
 * @param itemCheckEligibility Boolean indicating if the item is eligible based on CheckEligibilityAPI
 * @param eligibilityField Field to check for OMS return eligibility
 */
export const isItemEligible = (
  item: OrderDetailsLineItemSessionObj,
  itemCheckEligibility: boolean,
  eligibilityField: ReturnAllowedFields,
): boolean => {
  let omsReturnEligible = item[eligibilityField];
  if (eligibilityField === 'returnDropoffAllowed') {
    omsReturnEligible = Boolean(
      item.returnDropoffAllowed || item.returnOnlineAllowed,
    );
  }

  if (omsReturnEligible === undefined) {
    return Boolean(itemCheckEligibility);
  }
  return omsReturnEligible && itemCheckEligibility;
};

/**
 * Mapper function to maintain existing flow in <ItemsToReturn>
 * @param lineItems lineItems from selected fulfillment group
 * @param returnEligibilityDetails API response frmo return/eligibility
 * @returns SingleItemFromReturnsApi object
 */
export const mapToSingleItemFromReturnsApiList = (
  lineItems: OrderDetailsLineItemSessionObj[],
  returnEligibilityDetails: IReturnEligibilityResponse | undefined,
  returnMethod: ReturnMethodType,
): ReturnFormItem[] => {
  const checkEligibilityField = CheckEligibilityMap[returnMethod];
  const returnAllowedField = ReturnAllowedMap[returnMethod];
  const returnEligibilityLineItemMap = (
    returnEligibilityDetails?.lineItems || []
  ).reduce<Record<string, IReturnEligibilityResponse['lineItems'][number]>>(
    (itemMap, lineItem) => {
      return {
        ...itemMap,
        [lineItem.lineNumber]: lineItem,
      };
    },
    {},
  );

  return lineItems.map((item: OrderDetailsLineItemSessionObj) => {
    const itemCheckElig = getItemCheckEligibility(
      returnEligibilityDetails?.lineItems,
      item.lineId,
      checkEligibilityField,
    );
    const itemEligibility = isItemEligible(
      item,
      itemCheckElig,
      returnAllowedField,
    );
    return {
      brandName: item.brandName,
      department: item.departmentNumber,
      isDFCSourced: item.isDFCSourced,
      classNumber: item.classId,
      itemCost: item.unitPrice || 0,
      shippingCharge: item.shippingCharge || 0,
      unitCost: item.unitCost,
      itemDescription: item.description,
      itemImageURL: item.imageUrl,
      itemSubClass: item.subClassId,
      lineItemId: item.lineId,
      mVendorNumber: item.vendorNumber,
      vendorPartNumber: item.vendorPartNumber,
      poNumber: item.PONumber,
      quantityOrdered: item.originalOrderedQuantity,
      currentQuantity: item.currentQuantity,
      upcCode: item.upcCode,
      vendorName: item.vendorName,
      sku: item.skuNumber,
      omsId: item.omsId,
      secOmsId: item.secOmsId,
      returnEligibilityInfo: {
        returnEligible: itemEligibility,
      },
      hazmatItem: returnEligibilityLineItemMap[item.lineId]?.hazmatItem,
      chargeIdentifier: item.chargeIdentifier,
      totalPrice: item.totalPrice,
      fulfillmentVendorNumber: item.fulfillmentVendorNumber,
      itemWeight: item.itemWeight,
      productType: item.productType,
      returnWindowEndDate: item.returnWindowEndDate,
      relationType: item.relationType,
      isStatusDeliveredOrOrderPickedup:
        item.isStatusDeliveredOrOrderPickedup === undefined
          ? true
          : item.isStatusDeliveredOrOrderPickedup,
    };
  });
};

/**
 * Mapper function to transform the session item to the smaller SelectItem that we save to the state <ItemsToReturn>
 * @param lineItems lineItems from selected fulfillment group
 * @param returnEligibilityDetails API response frmo return/eligibility
 * @returns SingleItemFromReturnsApi object
 */
export const mapToReturnFormItem = (
  lineItems: OrderDetailsLineItemSessionObj[],
  returnEligibilityDetails: IReturnEligibilityResponse | undefined,
): ReturnFormItem[] => {
  const returnEligibilityLineItemMap = (
    returnEligibilityDetails?.lineItems || []
  ).reduce<Record<string, IReturnEligibilityResponse['lineItems'][number]>>(
    (itemMap, lineItem) => {
      return {
        ...itemMap,
        [lineItem.lineNumber]: lineItem,
      };
    },
    {},
  );

  return lineItems.map(
    (item: OrderDetailsLineItemSessionObj): ReturnFormItem => {
      return {
        brandName: item.brandName,
        department: item.departmentNumber,
        isDFCSourced: item.isDFCSourced,
        classNumber: item.classId,
        itemCost: item.unitPrice || 0,
        shippingCharge: item.shippingCharge || 0,
        unitCost: item.unitCost,
        itemDescription: item.description,
        itemImageURL: item.imageUrl,
        itemSubClass: item.subClassId,
        lineItemId: item.lineId,
        mVendorNumber: item.vendorNumber,
        vendorPartNumber: item.vendorPartNumber,
        poNumber: item.PONumber,
        quantityOrdered: item.originalOrderedQuantity,
        currentQuantity: item.currentQuantity,
        upcCode: item.upcCode,
        vendorName: item.vendorName,
        sku: item.skuNumber,
        omsId: item.omsId,
        secOmsId: item.secOmsId,
        eligibilities: getItemEligibilities(item, returnEligibilityDetails),
        hazmatItem: returnEligibilityLineItemMap[item.lineId]?.hazmatItem,
        chargeIdentifier: item.chargeIdentifier,
        totalPrice: item.totalPrice,
        fulfillmentVendorNumber: item.fulfillmentVendorNumber,
        itemWeight: item.itemWeight,
        productType: item.productType,
        returnWindowEndDate: item.returnWindowEndDate,
        relationType: item.relationType,
        isStatusDeliveredOrOrderPickedup:
          item.isStatusDeliveredOrOrderPickedup === undefined
            ? true
            : item.isStatusDeliveredOrOrderPickedup,
      };
    },
  );
};

/**
 * Mapper function to transform the session item to the smaller SelectItem that we save to the state <ItemsToReturn>
 * @param lineItems lineItems from selected fulfillment group
 * @param returnEligibilityDetails API response frmo return/eligibility
 * @returns SingleItemFromReturnsApi object
 */
export const mapToItemToReturn = (
  lineItems: OrderDetailsLineItemSessionObj[],
  returnEligibilityDetails: IReturnEligibilityResponse | undefined,
  isApoFpoEligible = false,
): ItemToReturn[] => {
  const returnEligibilityLineItemMap = (
    returnEligibilityDetails?.lineItems || []
  ).reduce<Record<string, IReturnEligibilityResponse['lineItems'][number]>>(
    (itemMap, lineItem) => {
      return {
        ...itemMap,
        [lineItem.lineNumber]: lineItem,
      };
    },
    {},
  );

  return lineItems.map((item: OrderDetailsLineItemSessionObj): ItemToReturn => {
    return {
      brandName: item.brandName,
      classNumber: item.classId,
      shippingCharge: item.shippingCharge || 0,
      depNbr: item.departmentNumber,
      mVendorNumber: item.vendorNumber,
      imageUrl: item.imageUrl,
      itemCost: item.unitPrice,
      unitCost: item.unitCost,
      itemSubClass: item.subClassId,
      itemDescription: item.description,
      lineItemId: item.lineId,
      omsId: item.omsId || item.secOmsId,
      upcCode: item.upcCode,
      poNumber: item.PONumber,
      isDFCSourced: item.isDFCSourced,
      vendorName: item.vendorName,
      vendorPartNumber: item.vendorPartNumber,
      itemImageURL: item.imageUrl,
      sku: item.skuNumber,
      chargeIdentifier: item.chargeIdentifier,
      totalPrice: item.totalPrice,
      fulfillmentVendorNumber: item.fulfillmentVendorNumber,
      itemWeight: item.itemWeight,
      productType: item.productType,
      returnWindowEndDate: item.returnWindowEndDate,
      hazmatItem: returnEligibilityLineItemMap[item.lineId]?.hazmatItem,
      // these are new now
      relationType: item.relationType,
      quantityOrdered: item.originalOrderedQuantity,
      currentQuantity: item.currentQuantity,
      eligibilities: getItemEligibilities(
        item,
        returnEligibilityDetails,
        isApoFpoEligible,
      ),
      isStatusDeliveredOrOrderPickedup:
        item.isStatusDeliveredOrOrderPickedup === undefined
          ? true
          : item.isStatusDeliveredOrOrderPickedup,
      // These get changed in the component
      returnDispCode: '',
      returnPolicyId: 0,
      returnQuantity: 1,
      returnReasonCode: '',
      rtvTokenId: '',
      returnReasonText: '',
    };
  });
};

const getItemCheckEligibility = (
  lineItems: IReturnEligibilityResponse['lineItems'] = [],
  lineId: string,
  eligibilityField: EligibilityItemFields,
): boolean => {
  const item = lineItems.find((lineItem) => lineItem.lineNumber === lineId);
  if (!item) return false;
  return Boolean(item[eligibilityField]);
};

/**
 * The "initial" O&P (Orders & Purchases) returnability check — the single
 * yes/no the order arrives with. An item passes if OMS allows a return by ANY
 * method. Under ROS only items that pass this are asked about in
 * detail (the v2 endpoint); an item is returnable iff it passes BOTH checks.
 */
export const isOmsInitialReturnable = (
  item: OrderDetailsLineItemSessionObj,
): boolean =>
  Boolean(
    item.returnOnlineAllowed ||
      item.returnDropoffAllowed ||
      item.returnInStoreAllowed ||
      item.returnLORPAllowed,
  );

/**
 * Adapts the ROS v2 eligibility response into the internal
 * IReturnEligibilityResponse shape so existing consumers keep working while the
 * migration switch (enableROS) is in play. Returnability comes straight from
 * v2; the fields v2 dropped are handled as follows (method-side ownership
 * lives with the Return Methods work):
 *  - per-line lorpReturnEligible <- v2 returnEligible (LORP method is then gated
 *    by returnability && OMS returnLORPAllowed downstream — a bridge)
 *  - per-line hazmatItem left undefined (weight/hazmat method rules move out)
 *  - order-level lorpOrderReturnEligible has no v2 source (LORP moved to the
 *    Return Methods endpoint) and is no longer read under ROS — in-store-only
 *    routing now reads the O&P LORP flag directly (checkIsInStoreOnlyReturn).
 *    Set false purely to satisfy the legacy response shape.
 */
export const adaptV2EligibilityResponse = (
  v2: IReturnEligibilityV2Response,
  net = '',
): IReturnEligibilityResponse => ({
  orderReturnEligible: v2.orderReturnEligible,
  sorisOrderReturnEligible: v2.sorisOrderReturnEligible,
  lorpOrderReturnEligible: false,
  lineItems: v2.lineItems.map((line) => ({
    lineNumber: line.lineNumber,
    returnEligible: line.returnEligible,
    sorisReturnEligible: line.sorisReturnEligible,
    lorpReturnEligible: line.returnEligible,
    hazmatItem: undefined,
  })),
  reasonCodes: v2.reasonCodes,
  et: v2.et,
  net,
});

/**
 * An empty eligibility answer — used under ROS when no item passes the initial
 * O&P check, so no detailed v2 call is made yet every item resolves to
 * "unavailable to return".
 */
export const emptyReturnEligibilityResponse =
  (): IReturnEligibilityResponse => ({
    orderReturnEligible: false,
    sorisOrderReturnEligible: false,
    lorpOrderReturnEligible: false,
    lineItems: [],
    reasonCodes: [],
    et: '',
    net: '',
  });

/**
 * Option types this migration offers, keyed by the wire value. A type absent
 * from this map is ignored by the adapter — which is how an option this
 * migration does not offer is dropped without disturbing the rest of its line.
 * In-store is deliberately absent: it stays on the OMS flag this phase.
 *
 * A Map rather than an object literal because the wire value is untrusted: a
 * plain object would resolve inherited keys such as 'constructor' or 'toString'
 * to truthy prototype members, which would pass the ignore check.
 */
/**
 * IN_STORE is the standard in-store return — the one this flow already calls
 * In-Store. It is not the whole in-store category despite the name: the service
 * treats going back to a store as one group covering both this and SORIS, but
 * only the standard option reaches the contract for now. SORIS arrives as its
 * own type in a later phase and maps to the SORIS method then; until it does,
 * SORIS availability stays on the source it has today.
 */
const OPTION_TYPE_TO_RETURN_METHOD = new Map<string, ReturnMethodType>([
  ['DROPOFF', RETURN_METHOD.UPS_DROP_OFF],
  ['PICKUP', RETURN_METHOD.LORP],
  ['IN_STORE', RETURN_METHOD.IN_STORE],
]);

/**
 * A single offered method, narrowed from the wire shape.
 * `optionId` has no consumer yet; it is the only per-method identifier the
 * contract gives, so dropping it here would make it unrecoverable.
 */
export interface AdaptedReturnMethod {
  optionId: string;
  eligible: boolean;
  shipToAddress?: ShipToAddress;
}

/**
 * `hazmat` is carried because the v2 eligibility adapter leaves `hazmatItem`
 * undefined, making this response the only place it can come from. The
 * item-list create payload reads it from here, which is where it was always
 * due; the shipment-shape builder still reads the item and so still sends
 * false for every item under ROS. `rtvItemId` is carried
 * unmapped, not because its destination is unknown — it is the RTV token under
 * the service's name, bound for the field that already holds that value — but
 * because the shipment leg it would travel on has nowhere to put it yet.
 * Slotting it into the legacy per-line trio would file it under a name it does
 * not answer to; the create rework is what gives it a home.
 */
export interface AdaptedReturnMethodsLineItem {
  lineItemId: string;
  hazmat: boolean;
  rtvItemId: string;
  methods: Partial<Record<ReturnMethodType, AdaptedReturnMethod>>;
}

/**
 * Keyed by lineItemId because every consumer is a lookup. Duplicate line ids
 * would last-write-win, matching how the other per-line maps in this file
 * behave; the contract does not produce them.
 */
export type AdaptedReturnMethods = Record<string, AdaptedReturnMethodsLineItem>;

/**
 * The contract carries contactName but no shipToName, and no emailId at all.
 * Both are left blank rather than invented — this is a return destination, not
 * the shopper, so neither may be filled from order or customer data. Leg
 * composition widens the shape and keeps them blank for the same reason.
 */
const adaptShipToAddress = (
  address: IReturnMethodsShipToAddress,
): ShipToAddress => ({
  shipToName: '',
  addressLine1: address.addressLine1,
  addressLine2: address.addressLine2,
  city: address.city,
  state: address.state,
  zipCode: address.postalCode,
  country: address.countryCode,
  contactName: address.contactName,
  dayPhone: address.dayPhone,
});

/**
 * Narrows the Return Methods response to the one question it can answer: for
 * each line, is parcel drop-off or large-item pickup offered, and to what
 * address. The remaining eligibility keys (in-store, SORIS, APO/FPO, and the
 * any-eligibility roll-up) have no source in this response and stay composed
 * in getItemEligibilities.
 *
 * The order-level verifyDecision is not returned: the fraud read happens on the
 * raw response, at the point the answer lands, rather than on this narrowed
 * shape.
 */
export const adaptReturnMethodsResponse = (
  response: IReturnMethodsResponse,
): AdaptedReturnMethods =>
  response.lineItems.reduce<AdaptedReturnMethods>(
    (adapted, line) => ({
      ...adapted,
      [line.lineItemId]: {
        lineItemId: line.lineItemId,
        hazmat: line.hazmat,
        rtvItemId: line.rtvItemId,
        methods: line.methods.reduce<
          Partial<Record<ReturnMethodType, AdaptedReturnMethod>>
        >((methods, method) => {
          const returnMethod = OPTION_TYPE_TO_RETURN_METHOD.get(method.type);
          if (!returnMethod) return methods;
          return {
            ...methods,
            [returnMethod]: {
              optionId: method.optionId,
              eligible: method.eligible,
              ...(method.shipToAddress && {
                shipToAddress: adaptShipToAddress(method.shipToAddress),
              }),
            },
          };
        }, {}),
      },
    }),
    {},
  );

/**
 * Folds the service's per-line answer into the eligibilities the method screens
 * read. Parcel drop-off, large-item pickup and in-store come from the service;
 * SORIS and APO/FPO keep the value composed at item selection.
 *
 * In-store is the service's to answer, and its answer means the standard
 * in-store return specifically. SORIS is a separate method here and is not in
 * the contract yet, so it keeps the source it has today and is untouched by the
 * answer — it moves across when the service carries a type of its own for it.
 *
 * APO/FPO deliberately stays on its existing value rather than being recomputed
 * from the service's drop-off answer. It is a client-side-only method, so an
 * answer about UPS parcel service should not withdraw it — doing so would
 * collapse a military-exchange order to in-store.
 *
 * anyEligibility is left alone on purpose: its only consumer runs at item
 * selection, before this call, so recomputing it here would be dead code.
 *
 * A line the service did not answer for resolves to unavailable for both
 * methods. That is a defensive floor, not a path the flow relies on: the caller
 * rejects an answer that skips a selected line before reaching here, because a
 * missing line and a line offered nothing look identical once folded in, and
 * only one of them should reach the customer as a set of methods.
 *
 * A method the customer already picked is cleared when that method's
 * eligibility comes out false, so a later answer cannot leave a line carrying a
 * method the service no longer offers. Without this the method card stops
 * rendering while the choice survives on the item, and the only per-item gate —
 * which asks whether a method is set, not whether it is still offered — lets it
 * through to submission.
 *
 * Note the rule reads the resulting value, not the answer: for the three keys
 * the service does not speak to, that value is the one composed at selection.
 * So a chosen in-store, SORIS or APO/FPO method survives this because those
 * keys were true when it was offered — not because anything here protects it.
 */
export const applyReturnMethodsToItems = (
  selectedItems: ItemToReturn[],
  adaptedMethods: AdaptedReturnMethods,
): ItemToReturn[] =>
  selectedItems.map((item) => {
    if (!item.eligibilities) return item;
    const methods = adaptedMethods[item.lineItemId]?.methods;
    const eligibilities = {
      ...item.eligibilities,
      [RETURN_METHOD.UPS_DROP_OFF]: Boolean(
        methods?.[RETURN_METHOD.UPS_DROP_OFF]?.eligible,
      ),
      [RETURN_METHOD.LORP]: Boolean(methods?.[RETURN_METHOD.LORP]?.eligible),
      [RETURN_METHOD.IN_STORE]: Boolean(
        methods?.[RETURN_METHOD.IN_STORE]?.eligible,
      ),
    };
    const keepsChosenMethod =
      !item.returnMethod || eligibilities[item.returnMethod];
    return {
      ...item,
      eligibilities,
      returnMethod: keepsChosenMethod ? item.returnMethod : undefined,
    };
  });

export const getItemEligibilities = (
  lineItem: OrderDetailsLineItemSessionObj | undefined,
  returnEligibilityDetails: IReturnEligibilityResponse | undefined,
  isApoFpoEligible = false,
): ItemEligibilities => {
  if (!returnEligibilityDetails || !lineItem)
    return {
      anyEligibility: false,
      [RETURN_METHOD.LORP]: false,
      [RETURN_METHOD.UPS_DROP_OFF]: false,
      [RETURN_METHOD.IN_STORE]: false,
      [RETURN_METHOD.SORIS]: false,
      [RETURN_METHOD.APO_FPO]: false,
    };

  const item = returnEligibilityDetails.lineItems.find(
    (line) => line.lineNumber === lineItem.lineId,
  );
  const allowSORIS = getFeatureSwitchStatic('allowSORIS');

  // ROS: returnability is the single consolidated answer — the
  // item passes the initial O&P check AND the detailed v2 check. Every method
  // is gated by it, so an item the detailed check finds not returnable shows as
  // unavailable regardless of OMS flags. The per-method split is a bridge until
  // methods move to ROS; the FE applies no returnability rule
  // of its own beyond the initial-check collapse.
  if (isROSEnabled()) {
    const returnable =
      isOmsInitialReturnable(lineItem) && Boolean(item?.returnEligible);
    const upsEligible =
      returnable &&
      Boolean(lineItem.returnDropoffAllowed || lineItem.returnOnlineAllowed);
    const instoreEligible =
      returnable && Boolean(lineItem.returnInStoreAllowed);
    const sorisEligible = Boolean(
      returnable &&
        allowSORIS &&
        lineItem.returnInStoreAllowed &&
        item?.sorisReturnEligible,
    );
    const lorpEligible = returnable && Boolean(lineItem.returnLORPAllowed);

    return {
      anyEligibility: returnable,
      [RETURN_METHOD.LORP]: lorpEligible,
      [RETURN_METHOD.UPS_DROP_OFF]: upsEligible,
      [RETURN_METHOD.IN_STORE]: instoreEligible,
      [RETURN_METHOD.SORIS]: sorisEligible,
      [RETURN_METHOD.APO_FPO]: Boolean(upsEligible && isApoFpoEligible),
    };
  }

  const upsEligible =
    item?.returnEligible &&
    (lineItem.returnDropoffAllowed || lineItem.returnOnlineAllowed);
  const instoreEligible = lineItem.returnInStoreAllowed;
  const sorisEligible = allowSORIS
    ? lineItem.returnInStoreAllowed && item?.sorisReturnEligible
    : false;
  const lorpEligible = item?.lorpReturnEligible && lineItem.returnLORPAllowed;

  return {
    anyEligibility: Boolean(
      upsEligible || lorpEligible || instoreEligible || sorisEligible,
    ),
    [RETURN_METHOD.LORP]: Boolean(lorpEligible),
    [RETURN_METHOD.UPS_DROP_OFF]: Boolean(upsEligible),
    [RETURN_METHOD.IN_STORE]: Boolean(instoreEligible),
    [RETURN_METHOD.SORIS]: Boolean(sorisEligible),
    [RETURN_METHOD.APO_FPO]: Boolean(upsEligible && isApoFpoEligible),
  };
};
